import type { SessionListItem } from '@heidi/session/contracts';
import { createContext, useContext } from 'react';

export interface RecentVisitHoverTarget {
  session: SessionListItem;
  /** The label the row renders, so the card cannot disagree with it. */
  sessionLabel: string;
  patientName: string | null | undefined;
}

/**
 * What a row can ask of the one shared card. Split from the host component so
 * that file exports a component and nothing else, which is what React Fast
 * Refresh needs to hot-swap it.
 */
export interface RecentVisitHoverCardController {
  /** `session_id` of the visit the card is showing, or null when it is down. */
  activeSessionId: string | null;
  isOpen: boolean;
  /** Show `target`, anchored at `row`. Instant while the card is already up. */
  requestOpen: (
    target: RecentVisitHoverTarget,
    row: HTMLLIElement,
    options?: { immediate?: boolean }
  ) => void;
  requestClose: () => void;
  cancelClose: () => void;
  close: () => void;
  contentRef: React.RefObject<HTMLDivElement>;
  /** Move keyboard focus into the card's first action. False when it is down. */
  focusFirstAction: () => boolean;
}

export const RecentVisitHoverCardContext =
  createContext<RecentVisitHoverCardController | null>(null);

export const useRecentVisitHoverCardController = (): RecentVisitHoverCardController => {
  const controller = useContext(RecentVisitHoverCardContext);
  if (!controller) {
    throw new Error(
      'RecentVisitRow must be rendered inside <RecentVisitHoverCardHost>. The ' +
        'card is one shared instance for the whole list, not one per row.'
    );
  }
  return controller;
};
